import {
  useCallback,
  useMemo,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { ContextMenuPanel, type MenuEntry } from '@viquard/doc-ui/components';
import type { CalendarEvent } from '../types.js';
import { buildEventContextMenu, type EventMenuActions } from '../utils/eventMenu.js';

export interface UseEventContextMenuResult {
  /** Spread onto the host element to capture right-click. */
  readonly bind: {
    readonly onContextMenu: (e: ReactMouseEvent<HTMLElement>) => void;
  };
  /** Render this somewhere in the tree. `null` when the menu is empty
   *  or disabled — host can render unconditionally without producing a
   *  stray portal. */
  readonly menu: ReactNode;
}

/**
 * Right-click menu for a single CalendarEvent. Internally uses doc-ui's
 * `ContextMenuPanel` (the lower-level building block of `ContextMenu`)
 * so we don't insert an extra wrapper `<div>` around absolute-positioned
 * event blocks. The host attaches `bind.onContextMenu` to the element
 * that should react to right-click; the host renders `menu` once
 * anywhere — the panel is rendered to a portal so DOM placement
 * doesn't matter.
 *
 * When `disabled` is true (or no actions yield any entries) the hook
 * returns `menu: null` and a no-op-on-empty handler — so the dense
 * MonthView grid doesn't allocate a portal per chip.
 */
export function useEventContextMenu(
  event: CalendarEvent,
  actions: EventMenuActions,
  options: { readonly disabled?: boolean; readonly ariaLabel?: string } = {},
): UseEventContextMenuResult {
  const { disabled = false, ariaLabel = 'Event actions' } = options;
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  const items: readonly MenuEntry[] = useMemo(
    () => (disabled ? [] : buildEventContextMenu({ event, ...actions })),
    [disabled, event, actions],
  );

  const onContextMenu = useCallback(
    (e: ReactMouseEvent<HTMLElement>) => {
      if (disabled || items.length === 0) return;
      e.preventDefault();
      e.stopPropagation();
      setPos({ x: Math.round(e.clientX), y: Math.round(e.clientY) });
    },
    [disabled, items.length],
  );

  const close = useCallback(() => { setPos(null); }, []);

  // Empty / disabled → no panel at all. Saves ~one portal + one event
  // listener per chip on a 30-day-month view that has 3 chips per cell.
  const menu =
    items.length === 0 ? null : (
      <ContextMenuPanel
        open={pos !== null}
        position={pos}
        items={items}
        onClose={close}
        aria-label={ariaLabel}
      />
    );

  return {
    bind: { onContextMenu },
    menu,
  };
}
